import React, { useCallback, useEffect, useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import type { HREmployee, HRShift } from './hrData';
import { usDate } from './licenseUtils';
import {
  clockIn, clockOut, endBreak, getTimesheet, localDateKey, startBreak, weekStartKey, type MealBreakAnswer,
} from '../../services/hrService';

/**
 * Clock in / breaks / clock out for the signed-in person — in the HR Center's
 * Clock tab and on My Day. Loads this week's shifts itself, so the hours and
 * overtime warning are always for the current week whatever week the
 * timesheet below is showing.
 *
 * Everyone is treated as hourly (non-exempt) for the overtime warning: the API
 * has no exempt flag yet. [NEEDS VERIFICATION — backend]
 */

interface Props {
  me: HREmployee;
  /** Bump to reload, e.g. after an admin corrects a shift. */
  refreshKey?: number;
  /** Called after a clock action succeeds, so the caller can reload too. */
  onChanged?: () => void;
}

const OVERTIME_MIN = 40 * 60;
const NEAR_OVERTIME_MIN = 36 * 60;
/** Clocking out after this many worked minutes asks about the meal break. */
const MEAL_QUESTION_AFTER_MIN = 5 * 60;

export const timeLabel = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
export const isoTime = (iso: string | null) => (iso ? timeLabel(new Date(iso)) : '—');
export const hoursLabel = (minutes: number) => `${(minutes / 60).toFixed(1)} hrs`;

/** Worked minutes — counted live (minus breaks) while a shift is still open. */
export const workedMinutes = (s: HRShift, now: Date) => {
  if (s.workedMinutes !== null) return s.workedMinutes;
  const openBreak = s.breakStartedAt ? (now.getTime() - new Date(s.breakStartedAt).getTime()) / 60000 : 0;
  return Math.max(0, (now.getTime() - new Date(s.clockInAt).getTime()) / 60000 - s.breakMinutes - openBreak);
};

const TimeClockCard: React.FC<Props> = ({ me, refreshKey = 0, onChanged }) => {
  const [shifts, setShifts] = useState<HRShift[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  const [asking, setAsking] = useState(false);
  const [mealNote, setMealNote] = useState('');

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    try {
      setShifts(await getTimesheet(weekStartKey()));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your shifts.');
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      setNow(new Date());
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const today = localDateKey(now);
  const weekStart = weekStartKey(now);
  const mine = (shifts ?? []).filter(s => s.employeeId === me.id);
  // Open shifts come back for any week, so "clocked in" never depends on the week.
  const myOpen = mine.find(s => !s.clockOutAt);
  const onBreak = !!myOpen?.breakStartedAt;
  const todayMinutes = mine.filter(s => s.workDate === today || s === myOpen).reduce((n, s) => n + workedMinutes(s, now), 0);
  const weekMinutes = mine.filter(s => s.workDate >= weekStart).reduce((n, s) => n + workedMinutes(s, now), 0);
  const overtime = weekMinutes >= OVERTIME_MIN;
  const nearOvertime = !overtime && weekMinutes >= NEAR_OVERTIME_MIN;

  const requestClockOut = () => {
    if (myOpen && workedMinutes(myOpen, now) >= MEAL_QUESTION_AFTER_MIN) setAsking(true);
    else run(() => clockOut());
  };
  const answerMeal = (taken: boolean) => {
    const meal: MealBreakAnswer = { mealBreakTaken: taken };
    if (!taken && mealNote.trim()) meal.mealBreakNote = mealNote.trim();
    setAsking(false);
    setMealNote('');
    run(() => clockOut(meal));
  };

  return (
    <div className="hr-clock__card">
      {error && (
        <div className="hr__alert hr-clock__alert" role="alert">
          <span>{error}</span>
          <button type="button" className="hr__alert-action" onClick={() => (shifts ? setError(null) : load())}>
            {shifts ? 'Dismiss' : 'Retry'}
          </button>
        </div>
      )}
      <div className="hr-clock__status">{myOpen ? (onBreak ? 'On Break' : 'Clocked In') : 'Clocked Out'}</div>
      <div className="hr-clock__icon" aria-hidden="true">⏰</div>
      <div className="hr-clock__now-label">Currently it's</div>
      <div className="hr-clock__now">{timeLabel(now)}</div>
      {myOpen && (
        <div className="hr-clock__elapsed">
          {onBreak && myOpen.breakStartedAt
            ? `On break since ${isoTime(myOpen.breakStartedAt)}`
            : `You have clocked in since ${isoTime(myOpen.clockInAt)}`}
        </div>
      )}
      {myOpen && myOpen.workDate !== today && (
        <p className="hr-clock__warn">
          <FiAlertTriangle size={12} aria-hidden="true" />
          You didn&apos;t clock out on {usDate(myOpen.workDate)}. End this shift, then ask your admin to correct it.
        </p>
      )}
      <div className="hr-clock__today">Worked today: <strong>{hoursLabel(todayMinutes)}</strong></div>

      {asking ? (
        <div className="hr-clock__ask" role="group" aria-label="Meal break check">
          <p className="hr-clock__ask-q">Did you get your full, uninterrupted meal break today?</p>
          <input
            className="hr-clock__ask-note"
            value={mealNote}
            onChange={e => setMealNote(e.target.value)}
            placeholder="If not, what happened? (optional)"
            aria-label="Why you missed your meal break"
          />
          <div className="hr-clock__actions">
            <button type="button" className="hr-clock__break" disabled={busy} onClick={() => answerMeal(false)}>No</button>
            <button type="button" className="hr-clock__end" disabled={busy} onClick={() => answerMeal(true)}>Yes, end shift</button>
          </div>
        </div>
      ) : (
        <div className="hr-clock__actions">
          {myOpen ? (
            <>
              <button type="button" className="hr-clock__break" disabled={busy || !shifts} onClick={() => run(onBreak ? endBreak : startBreak)}>
                {onBreak ? 'End Break' : 'Take a Break'}
              </button>
              <button type="button" className="hr-clock__end" disabled={busy || !shifts} onClick={requestClockOut}>End Shift</button>
            </>
          ) : (
            <button type="button" className="hr-clock__end hr-clock__end--start" disabled={busy || !shifts} onClick={() => run(() => clockIn())}>
              Clock In
            </button>
          )}
        </div>
      )}

      <div className="hr-clock__week-total">
        <span>This week</span>
        <strong>{hoursLabel(weekMinutes)}</strong>
        <span className="hr-clock__week-track" aria-hidden="true">
          <span
            className={`hr-clock__week-fill${overtime ? ' hr-clock__week-fill--over' : nearOvertime ? ' hr-clock__week-fill--near' : ''}`}
            style={{ width: `${Math.min(100, (weekMinutes / OVERTIME_MIN) * 100)}%` }}
          />
        </span>
      </div>
      {overtime && (
        <p className="hr-clock__warn">
          <FiAlertTriangle size={12} aria-hidden="true" />
          You&apos;re in overtime this week ({hoursLabel(weekMinutes - OVERTIME_MIN)} over 40).
        </p>
      )}
      {nearOvertime && <p className="hr-clock__note">{hoursLabel(OVERTIME_MIN - weekMinutes)} until overtime this week.</p>}
    </div>
  );
};

export default TimeClockCard;
